import { ChangeDetectionStrategy, Component, Input, ViewChild } from '@angular/core';
import { NgClass } from '@angular/common';
import { HeroGalleryItemDto, ImageRefDto } from '../../shared/models/marketing.models';
import { parseHeroTitleMarkup, type HeroTitleLine } from '../utils/hero-title-markup';
import { PhotoGalleryLightboxComponent } from './photo-gallery-lightbox.component';

/** Default hero card image when no gallery cover is configured. */
export const HERO_CARD_PLACEHOLDER_URL = '/assets/images/students.webp';

export type MarketingHeroModel = {
  variant: 'home' | 'country';
  badge?: string;
  eyebrow?: string;
  title?: string;
  lede?: string;
  features?: string[];
  primaryCta?: { text?: string; href?: string };
  backgroundImage?: ImageRefDto;
  artImage?: ImageRefDto;
  gallery?: HeroGalleryItemDto[];
  /** Country pages / legacy */
  image?: ImageRefDto;
  sideBadge?: { value?: string; label?: string };
};

@Component({
  selector: 'app-marketing-hero',
  standalone: true,
  imports: [NgClass, PhotoGalleryLightboxComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    style: 'display: block; width: 100%; max-width: 100%;',
  },
  template: `
    <section class="cg-mkt-hero" [ngClass]="{ 'cg-mkt-hero--country': hero.variant === 'country' }">
      <div class="cg-mkt-hero__bg" aria-hidden="true">
        @if (heroBackground(); as bg) {
          <img [src]="bg.cdnUrl!" [alt]="bg.alt ?? ''" loading="eager" />
        }
      </div>
      <div class="cg-mkt-hero__inner" [ngClass]="{ 'cg-mkt-hero__inner--single': hero.variant === 'country' }">
        <div class="cg-mkt-hero__copy">
          @if (hero.variant === 'home' && hero.badge) {
            <span class="cg-mkt-hero__badge">
              <span class="cg-mkt-hero__badge-dot" aria-hidden="true"></span>
              {{ hero.badge }}
            </span>
          }
          @if (hero.eyebrow) {
            <div class="cg-mkt-hero__eyebrow">{{ hero.eyebrow }}</div>
          }
          @if (heroTitleLines().length) {
            <h1 class="cg-mkt-hero__title">
              @for (line of heroTitleLines(); track $index; let lastLine = $last) {
                @for (segment of line; track $index) {
                  @if (segment.type === 'highlight') {
                    <span class="cg-mkt-hero__highlight">{{ segment.value }}</span>
                  } @else {
                    {{ segment.value }}
                  }
                }
                @if (!lastLine) {
                  <br />
                }
              }
            </h1>
          }
          @if (hero.lede) {
            <p class="cg-mkt-hero__lede">{{ hero.lede }}</p>
          }
          @if (hero.features?.length) {
            <ul class="cg-mkt-hero__features">
              @for (f of hero.features; track f) {
                <li>
                  <span class="cg-mkt-hero__check" aria-hidden="true">✓</span>
                  {{ f }}
                </li>
              }
            </ul>
          }
          <div class="cg-mkt-hero__actions">
            @if (hero.primaryCta?.text) {
              <a class="cg-mkt-btn cg-mkt-btn--accent cg-mkt-btn--lg" [href]="hero.primaryCta?.href ?? '#cta'">{{ hero.primaryCta?.text }}</a>
            }
          </div>
        </div>
        @if (hero.variant === 'home') {
          <div class="cg-mkt-hero__art">
            <div class="cg-mkt-hero-card cg-mkt-hero-card--back" aria-hidden="true"></div>
            @if (hasGallery()) {
              <button
                type="button"
                class="cg-mkt-hero-card cg-mkt-hero-card--front cg-mkt-hero-card--interactive"
                aria-label="Open photo gallery"
                (click)="openGallery()"
              >
                <img
                  [src]="coverImageUrl()"
                  [alt]="hero.artImage?.alt ?? ''"
                  loading="eager"
                  (error)="onCoverImageError($event)"
                />
                <span class="cg-mkt-hero-card__gallery-hint" aria-hidden="true">
                  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                    <path d="M4 5h2l1-2h10l1 2h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zm8 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
                  </svg>
                </span>
              </button>
            } @else {
              <div class="cg-mkt-hero-card cg-mkt-hero-card--front">
                <img
                  [src]="coverImageUrl()"
                  [alt]="hero.artImage?.alt ?? ''"
                  loading="eager"
                  (error)="onCoverImageError($event)"
                />
              </div>
            }
            @if (hero.sideBadge?.value || hero.sideBadge?.label) {
              <div class="cg-mkt-hero-side-badge">
                @if (hero.sideBadge?.value) {
                  <div class="cg-mkt-hero-side-badge__value">{{ hero.sideBadge?.value }}</div>
                }
                @if (hero.sideBadge?.label) {
                  <div class="cg-mkt-hero-side-badge__label">{{ hero.sideBadge?.label }}</div>
                }
              </div>
            }
          </div>
        }
      </div>
    </section>
    <div class="cg-mkt-hero-mobile-cta">
      <a
        class="cg-mkt-btn cg-mkt-btn--primary"
        [href]="hero.primaryCta?.href ?? '#cta'"
        >{{ hero.primaryCta?.text ?? 'Get started' }}</a
      >
    </div>

    @if (hasGallery()) {
      <app-photo-gallery-lightbox #gallery [items]="hero.gallery!" />
    }
  `,
})
export class MarketingHeroComponent {
  @Input({ required: true }) hero!: MarketingHeroModel;

  @ViewChild('gallery') private galleryRef?: PhotoGalleryLightboxComponent;

  private coverFallback = false;

  heroBackground(): ImageRefDto | undefined {
    return this.hero.backgroundImage ?? this.hero.image;
  }

  heroTitleLines(): HeroTitleLine[] {
    return this.hero.title ? parseHeroTitleMarkup(this.hero.title) : [];
  }

  hasGallery(): boolean {
    return (this.hero.gallery?.length ?? 0) > 0;
  }

  coverImageUrl(): string {
    if (this.coverFallback || !this.hero.artImage?.cdnUrl) {
      return HERO_CARD_PLACEHOLDER_URL;
    }
    return this.hero.artImage.cdnUrl;
  }

  openGallery(): void {
    const coverUrl = this.hero.artImage?.cdnUrl;
    const startIndex = coverUrl
      ? Math.max(
          0,
          (this.hero.gallery ?? []).findIndex((item) => item.image.cdnUrl === coverUrl),
        )
      : 0;
    this.galleryRef?.show(startIndex >= 0 ? startIndex : 0);
  }

  onCoverImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    if (img.src.includes(HERO_CARD_PLACEHOLDER_URL)) return;
    this.coverFallback = true;
    img.src = HERO_CARD_PLACEHOLDER_URL;
  }
}
